import { useRef, useEffect, useCallback, useState } from 'react';
import { Btn } from '../ui/index';

const STROKE = '#0f172a';
const STROKE_WIDTH = 2.2;

function getPoint(canvas, evt) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const source = evt.touches ? evt.touches[0] : evt;
  return {
    x: (source.clientX - rect.left) * scaleX,
    y: (source.clientY - rect.top) * scaleY,
  };
}

/** Canvas signature pad — exports PNG data-URL (no external library). */
export default function SignaturePad({ width = 520, height = 180, onChange, initialDataUrl }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  const notifyChange = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange?.(hasInkRef.current ? canvas.toDataURL('image/png') : null);
  }, [onChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setEmpty(true);
    onChange?.(null);
  }, [onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE;
    ctx.lineWidth = STROKE_WIDTH;

    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        hasInkRef.current = true;
        setEmpty(false);
      };
      img.src = initialDataUrl;
    }
  }, [initialDataUrl]);

  const startDraw = (evt) => {
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    const { x, y } = getPoint(canvas, evt);
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { x, y } = getPoint(canvas, evt);
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInkRef.current = true;
    setEmpty(false);
  };

  const endDraw = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    drawingRef.current = false;
    notifyChange();
  };

  return (
    <div>
      <div
        style={{
          border: '2px dashed #94a3b8',
          borderRadius: 12,
          background: '#fff',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ display: 'block', width: '100%', height: 'auto', cursor: 'crosshair' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          {empty ? 'با ماوس یا لمس امضا بکشید' : 'امضا آماده ذخیره است'}
        </span>
        <Btn variant="secondary" onClick={clear} style={{ padding: '6px 14px', fontSize: 12 }}>
          پاک کردن
        </Btn>
      </div>
    </div>
  );
}
